/**
 * negative-scanner / index.js — the page.
 *
 * THREE PLACES, ONE PIPELINE
 * ──────────────────────────
 *   start — film type, the two doors (camera / files), how to light the film;
 *   live  — the camera shows the POSITIVE at frame rate (gl.js), the frame on
 *           the film is found and outlined, colours follow it; the shutter
 *           takes a still (full sensor where ImageCapture exists);
 *   edit  — one frame: crop corners, rotation, mirror, colour, levels, dust,
 *           save. The film strip below holds every frame of the session and
 *           outlives a reload (store.js).
 * Live, edit and export all apply the same table from convert.js, so what the
 * viewfinder shows is what the file contains.
 *
 * THE DAILY CAP counts SAVED FRAMES (config/tiers.php, 2 without Премиум).
 * Looking, adjusting and re-saving are free: every frame carries a limit key
 * stamped ONCE, when it is captured or cut from a scan, so saving it again in
 * another format or after another edit is the same unit — the file's own
 * name/size/mtime would change on every save and bill it again (CLAUDE.md,
 * «единица — пережить перезапись файла»). A frame that was ever delivered is
 * marked `paid` and never asks the counter again.
 */
'use strict';

import * as C from './convert.js';
import * as FR from './frames.js';
import { removeDust } from './dust.js';
import { createGlLive, createCpuLive, outToSrcNorm, orientMatrix } from './gl.js';
import * as DB from './store.js';

const CFG = window.NS_CFG || {};
const SLUG = 'negative-scanner';
const T = (k, a) => {
    let s = (CFG.t && CFG.t[k] != null) ? CFG.t[k] : k;
    // longest placeholder first: ':name' must not be eaten by ':n'
    if (a) for (const key of Object.keys(a).sort((x, y) => y.length - x.length)) s = s.split(':' + key).join(String(a[key]));
    return s;
};
const $ = (id) => document.getElementById(id);
const toast = (m, kind) => { try { window.__toast ? window.__toast(m, kind || 'info') : console.log('[toast]', m); } catch (_) {} };
const stage = (n, d) => { try { window.__stage && window.__stage(n, d); } catch (_) {} };
const funnel = (n, d) => { try { window.__aeFunnel && window.__aeFunnel(n, d); } catch (_) {} };
const diag = (d) => { try { window.__aeDiagnostic && window.__aeDiagnostic(d); } catch (_) {} };
const complete = (d) => { try { window.__aeToolComplete && window.__aeToolComplete(d); } catch (_) {} };
const pref = (k, d) => { try { const ST = window.__toolSettings; return ST ? ST.load(SLUG, k, d) : d; } catch (_) { return d; } };
const setPref = (k, v) => { try { const ST = window.__toolSettings; ST && ST.save(SLUG, k, v); } catch (_) {} };
const TL = () => window.__toolLimit || null;

const MEM = (typeof window !== 'undefined' && window.__memBudget) ? window.__memBudget.gb() : 4;
/** Longest side a stored source keeps: a flatbed scan of a strip can be
 *  12 000 px, and the export never needs more than the frame it cuts out. */
const SRC_MAX = MEM >= 8 ? 9000 : MEM >= 4 ? 7000 : 5000;
/** The editor works on a downscaled copy; the export on the full source. */
const PREV_SRC = 2200;
/** iOS refuses canvases past ~16.7 Mpx (and says so by drawing nothing), so
 *  a stored source there is capped by AREA as well as by side. */
const IOS = (() => {
    try { return /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); } catch (_) { return false; }
})();
const AREA_MAX = IOS ? 16e6 : 100e6;
const PREV_OUT = 1500;
const ANALYZE = 900;
const LIVE_GRAB = 256;

const S = {
    film: 'color',
    view: 'start',
    fmt: 'jpg',
    keepShooting: false,
    busy: false,
    frames: [],
    live: null,
    cur: null,        // {rec, src:{bmp,w,h}, prev:{data,w,h,scale}, warped, lut, mode:'look'|'crop'|'pickBase'|'pickGrey'}
    raw: false,
    srcCache: new Map(),
};

/* ─────────────────────────────── helpers ─────────────────────────────── */

const uid = () => {
    const a = new Uint8Array(8);
    try { crypto.getRandomValues(a); } catch (_) { for (let i = 0; i < 8; i++) a[i] = (Math.random() * 256) | 0; }
    return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
};
const clone = (o) => JSON.parse(JSON.stringify(o));
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));
const show = (el, on = true) => { if (el) el.hidden = !on; };
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const neg = () => S.film !== 'slide';

function busy(msg) {
    const b = $('nsBusy');
    if (!b) return;
    if (msg) { $('nsBusyTxt').textContent = msg; b.hidden = false; } else b.hidden = true;
}
function hud(msg, kind) {
    const h = $('nsHud');
    if (!h) return;
    if (!msg) { h.hidden = true; return; }
    h.textContent = msg;
    h.className = 'ns-hud' + (kind ? ' is-' + kind : '');
    h.hidden = false;
}

function canvasOf(w, h) { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; }

function decodeBlob(blob) {
    if (window.createImageBitmap) {
        return createImageBitmap(blob, { imageOrientation: 'from-image' }).catch(() => imgDecode(blob));
    }
    return imgDecode(blob);
}
function imgDecode(blob) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const im = new Image();
        im.onload = () => { URL.revokeObjectURL(url); resolve(im); };
        im.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
        im.src = url;
    });
}
const dimsOf = (b) => ({ w: b.naturalWidth || b.width, h: b.naturalHeight || b.height });

function toBlob(canvas, type, q) {
    return new Promise((resolve, reject) => {
        try { canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode'))), type, q); } catch (e) { reject(e); }
    });
}

/** Draw a bitmap into a canvas no longer than `max` on its long side (and no
 *  larger than AREA_MAX). `noData` — only the canvas is wanted: reading the
 *  pixels of a 9000 px source back would cost 300 MB for nothing. */
function scaled(bmp, max, noData) {
    const d = dimsOf(bmp);
    const k = Math.min(1, max / Math.max(d.w, d.h), Math.sqrt(AREA_MAX / (d.w * d.h)));
    const c = canvasOf(Math.max(1, Math.round(d.w * k)), Math.max(1, Math.round(d.h * k)));
    const g = c.getContext('2d', { willReadFrequently: !noData });
    g.imageSmoothingQuality = 'high';
    g.drawImage(bmp, 0, 0, c.width, c.height);
    return { canvas: c, data: noData ? null : g.getImageData(0, 0, c.width, c.height).data, w: c.width, h: c.height, scale: c.width / d.w };
}

/* ──────────────────────────── the parameters ─────────────────────────── */

function paramsOf(rec, levels) {
    return {
        mode: rec.film,
        wb: rec.film === 'slide' && rec.wb === 'base' ? 'auto' : rec.wb,
        base: rec.base,
        levels: levels || rec.levels,
        adjust: rec.adjust,
    };
}

/** Levels of a (warped) frame: block-averaged stats, a hair inside the edges. */
function levelsOf(data, w, h, film) {
    const q = C.insetQuad(FR.fullQuad(w, h), 0.03);
    const hs = C.histograms(data, w, h, { quad: q, box: 2, clipAt: film === 'slide' ? 256 : 254 });
    return C.computeLevels(hs, film);
}

/* ─────────────────────────────── film type ───────────────────────────── */

function setFilm(f, from) {
    if (!C.MODES.includes(f)) f = 'color';
    S.film = f;
    setPref('film', f);
    document.querySelectorAll('#nsFilm [data-film]').forEach((b) => {
        const on = b.dataset.film === f;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    if (S.live) { S.live.levels = null; if (f === 'slide' && S.live.wb === 'base') S.live.wb = 'auto'; syncLiveWb(); }
    if (S.cur && from === 'user') {
        const rec = S.cur.rec;
        rec.film = f;
        if (f === 'slide' && rec.wb === 'base') rec.wb = 'auto';
        rec.levels = null;
        refreshEdit(true);
        syncEditControls();
        persist(rec);
    }
    syncTexts();
    if (from === 'user') funnel('film', { film: f });
}

function syncTexts() {
    const h = $('nsLiveHint');
    if (h) h.textContent = T(S.film === 'slide' ? 'live_hint_slide' : 'live_hint');
}

/* ─────────────────────────────── the camera ──────────────────────────── */

function openCamera() {
    funnel('input', { method: 'camera' });
    goView('live');
    startCamera();
}

function startCamera(opts = {}) {
    stopCamera();
    hideCamFail();
    const L = S.live = {
        mgr: S.live && S.live.mgr, stream: null, track: null, raf: 0, renderer: null,
        levels: null, lock: false, wb: 'auto', base: null, autoBase: null,
        rot: +pref('rot', 0) || 0, flip: !!pref('flip', false),
        quad: null, quadSeen: 0, lastAnalyze: 0, clip: 0, pick: null,
        facing: opts.facing || (S.live && S.live.facing) || 'environment', deviceId: opts.deviceId || null,
    };
    syncLiveControls();
    if (!L.mgr) L.mgr = new window.CameraManager({ slug: SLUG });
    if (!L.mgr.isSupported || !L.mgr.isSupported()) {
        showCamFail('not_supported', (L.mgr.getErrorMessage && L.mgr.getErrorMessage('not_supported')) || T('cam_fail'));
        return;
    }
    L.opening = true;
    hud(T('cam_starting'));
    const want = L.deviceId ? { deviceId: L.deviceId, width: 1920, height: 1080 } : { facingMode: L.facing, width: 1920, height: 1080 };
    // ui:false — the door the person pressed is gone by now (the start panel
    // hides), so the refusal is said INSIDE the viewfinder by showCamFail(),
    // with the manager's own classified sentence.
    L.mgr.start(Object.assign({ ui: false }, want)).then((stream) => {
        if (S.live !== L || !L.opening) { stream.getTracks().forEach((t) => t.stop()); return; }
        L.opening = false;
        L.stream = stream;
        L.track = stream.getVideoTracks()[0];
        const v = $('nsVideo');
        v.srcObject = stream;
        const go = () => { const p = v.play(); if (p && p.catch) p.catch(() => {}); setupCaps(); loopLive(); };
        if (v.readyState >= 1) go(); else v.onloadedmetadata = go;
        stage('camera_opened', { facing: L.facing });
        watchFirstFrame(v, L);
        try { window.__wakeLock && window.__wakeLock.hold('negative-scanner-camera'); } catch (_) {}
        hud(T('live_hint_short'));
    }).catch((err) => {
        if (S.live !== L) return;
        L.opening = false;
        const CM = window.CameraManager || {};
        const code = (err && err.code) || (CM.classify ? CM.classify(err) : 'generic');
        const msg = (L.mgr.getErrorMessage && L.mgr.getErrorMessage(code)) || T('cam_fail');
        diag({ issue: 'camera_failed', code, name: CM.causeName ? CM.causeName(err) : String((err && err.name) || '?') });
        showCamFail(code, msg);
    });
}

/** start() resolving is not frames arriving: a held camera or a privacy
 *  shutter gives a live track that never paints. */
function watchFirstFrame(v, L) {
    clearTimeout(L.frameT);
    L.frameT = setTimeout(() => {
        if (S.live !== L || !L.stream) return;
        if (v.videoWidth > 0) return;
        diag({ issue: 'camera_no_frames' });
        stopCamera();
        showCamFail('unresponsive', T('cam_no_frames'));
    }, 6500);
}

function stopCamera() {
    const L = S.live;
    if (!L) return;
    if (L.raf) cancelAnimationFrame(L.raf);
    L.raf = 0;
    clearTimeout(L.frameT);
    L.opening = false;
    try { L.mgr && L.mgr.stop && L.mgr.stop(); } catch (_) {}
    if (L.stream) L.stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
    L.stream = null; L.track = null;
    const v = $('nsVideo'); if (v) { try { v.srcObject = null; } catch (_) {} }
    try { window.__wakeLock && window.__wakeLock.releaseHold('negative-scanner-camera'); } catch (_) {}
}

function showCamFail(code, msg) {
    hud(null);
    $('nsCamFailMsg').textContent = msg;
    const how = $('nsCamFailHow');
    const hint = code === 'denied' ? T('camfail_denied') : code === 'busy' ? T('camfail_busy') : '';
    how.textContent = hint; how.hidden = !hint;
    $('nsCamFail').hidden = false;
    $('nsLive').hidden = true;
    show($('nsShootBar'), false);
    show($('nsLiveCtl'), false);
    funnel('camera_failed_panel', { code });
}
function hideCamFail() { const b = $('nsCamFail'); if (b) b.hidden = true; }

function capsOf() {
    const t = S.live && S.live.track;
    try { return (t && t.getCapabilities) ? t.getCapabilities() : {}; } catch (_) { return {}; }
}
function setupCaps() {
    const caps = capsOf();
    const L = S.live;
    const zw = $('nsZoomRow'), ew = $('nsExpRow');
    if (caps.zoom && caps.zoom.max > caps.zoom.min) {
        const z = $('nsZoom');
        z.min = caps.zoom.min; z.max = Math.min(caps.zoom.max, 8); z.step = caps.zoom.step || 0.1;
        z.value = (L.track.getSettings && L.track.getSettings().zoom) || caps.zoom.min;
        $('nsZoomOut').textContent = (+z.value).toFixed(1) + '×';
        show(zw, true);
    } else show(zw, false);
    if (caps.exposureCompensation && caps.exposureCompensation.max > caps.exposureCompensation.min) {
        const e = $('nsExp');
        e.min = caps.exposureCompensation.min; e.max = caps.exposureCompensation.max; e.step = caps.exposureCompensation.step || 0.1;
        e.value = (L.track.getSettings && L.track.getSettings().exposureCompensation) || 0;
        $('nsExpOut').textContent = (+e.value).toFixed(1);
        show(ew, true);
    } else show(ew, false);
    try {
        if (caps.focusMode && caps.focusMode.includes('continuous')) L.track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {});
    } catch (_) {}
    if (L.mgr && L.mgr.enumerate) L.mgr.enumerate().then((list) => show($('nsSwitch'), (list || []).length > 1)).catch(() => {});
}
function applyCam(c) {
    const t = S.live && S.live.track;
    if (!t || !t.applyConstraints) return;
    t.applyConstraints({ advanced: [c] }).catch(() => {});
}

/* ─────────────────────────────── live view ───────────────────────────── */

let grab = null;
function loopLive() {
    const L = S.live;
    if (!L) return;
    const v = $('nsVideo');
    const cv = $('nsLive');
    const step = (ts) => {
        if (S.live !== L || !L.stream) return;
        L.raf = requestAnimationFrame(step);
        if (!v.videoWidth || v.readyState < 2) return;
        // canvas size = the oriented video, capped
        const vw = v.videoWidth, vh = v.videoHeight;
        const ow = (L.rot % 180) ? vh : vw, oh = (L.rot % 180) ? vw : vh;
        const k = Math.min(1, (L.renderer && L.renderer.kind === 'cpu' ? 640 : 1920) / Math.max(ow, oh));
        const cw = Math.round(ow * k), ch = Math.round(oh * k);
        if (!L.renderer || L.renderer.lost) makeRenderer(cv);
        if (cv.width !== cw || cv.height !== ch) { cv.width = cw; cv.height = ch; }
        if (cv.hidden) { cv.hidden = false; hideCamFail(); show($('nsShootBar'), true); show($('nsLiveCtl'), true); }
        if (!L.lastAnalyze || ts - L.lastAnalyze > 280) { L.lastAnalyze = ts; analyzeLive(v); }
        if (!L.lut) return;
        const ok = L.renderer.draw(v, { rot: L.rot, flip: L.flip, bw: S.film === 'bw', sat: S.liveAdj.sat, raw: S.raw });
        if (!ok && L.renderer.kind === 'gl') { L.renderer = null; }
        drawLiveOverlay();
    };
    L.raf = requestAnimationFrame(step);
}

function makeRenderer(cv) {
    const L = S.live;
    let r = null;
    if (!window.__NS_NO_GL) { try { r = createGlLive(cv); } catch (_) { r = null; } }
    if (!r) {
        // A canvas that already handed out a WebGL context cannot give a 2D
        // one — swap in a fresh element.
        if (L.renderer && L.renderer.kind === 'gl') {
            const fresh = cv.cloneNode(false);
            cv.parentNode.replaceChild(fresh, cv);
            cv = fresh;
        }
        r = createCpuLive(cv);
        diag({ issue: 'live_cpu_renderer' });
    }
    L.renderer = r;
    if (L.lut) r.setLut(L.lut);
    funnel('live_renderer', { kind: r.kind });
}

function guideQuad(w, h) {
    return [{ x: w * 0.08, y: h * 0.08 }, { x: w * 0.92, y: h * 0.08 }, { x: w * 0.92, y: h * 0.92 }, { x: w * 0.08, y: h * 0.92 }];
}

function smoothQuad(prev, q) {
    if (!prev) return q;
    const d = Math.max(...q.map((p, i) => Math.hypot(p.x - prev[i].x, p.y - prev[i].y)));
    if (d > 25) return q;          // moved to another frame: jump
    return q.map((p, i) => ({ x: prev[i].x * 0.55 + p.x * 0.45, y: prev[i].y * 0.55 + p.y * 0.45 }));
}

function analyzeLive(v) {
    const L = S.live;
    const vw = v.videoWidth, vh = v.videoHeight;
    const k = LIVE_GRAB / Math.max(vw, vh);
    const gw = Math.max(16, Math.round(vw * k)), gh = Math.max(16, Math.round(vh * k));
    if (!grab) grab = canvasOf(gw, gh);
    if (grab.width !== gw || grab.height !== gh) { grab.width = gw; grab.height = gh; }
    const g = grab.getContext('2d', { willReadFrequently: true });
    if (!window.__drawCameraFrame(g, v, 0, 0, gw, gh)) return;   // no frame this tick
    const img = g.getImageData(0, 0, gw, gh);
    L.grab = { data: img.data, w: gw, h: gh };
    let det = null;
    try { det = FR.detectFrames(img.data, gw, gh, S.film); } catch (_) { det = null; }
    const now = performance.now();
    const q = det && det.frames.length ? FR.centralFrame(det.frames, gw, gh) : null;
    if (q) { L.quad = smoothQuad(L.quad, q); L.quadSeen = now; }
    else if (now - L.quadSeen > 1200) L.quad = null;
    if (det && det.base) L.autoBase = det.base;
    const region = L.quad ? C.insetQuad(L.quad, 0.08) : guideQuad(gw, gh);
    const hs = C.histograms(img.data, gw, gh, { quad: region, clipAt: neg() ? 254 : 256 });
    L.clip = hs.total ? hs.clipped / hs.total : 0;
    if (!L.lock || !L.levels) {
        const nl = C.computeLevels(hs, S.film);
        if (!L.levels) L.levels = nl;
        else for (const key of ['lo', 'hi']) for (let c = 0; c < 3; c++) L.levels[key][c] = L.levels[key][c] * 0.55 + nl[key][c] * 0.45;
    }
    L.lut = C.buildLut({ mode: S.film, wb: L.wb === 'base' && L.base && neg() ? 'base' : 'auto', base: L.base, levels: L.levels, adjust: S.liveAdj });
    if (L.renderer) L.renderer.setLut(L.lut);
    // what to tell the person
    if (L.pick === 'base') hud(T('tap_base_live'), 'warn');
    else if (neg() && L.clip > 0.04) hud(T('too_bright'), 'warn');
    else if (L.quad) hud(T('frame_found'), 'good');
    else hud(T('live_hint_short'));
}

/** Normalised output point → normalised source point for the LIVE view. */
function liveOutToSrc(x, y) { const L = S.live; return outToSrcNorm(L.rot, L.flip, x, y); }
function liveSrcToOut(x, y) {
    const L = S.live;
    const m = orientMatrix(L.rot, L.flip);
    const a = m[0], b = m[3], c = m[6], d = m[1], e = m[4], f = m[7];
    const det = a * e - b * d;
    const X = x - c, Y = y - f;
    return { x: (e * X - b * Y) / det, y: (-d * X + a * Y) / det };
}

function placeOverlay(target) {
    const ov = $('nsOver'), st = $('nsStage');
    if (!target || target.hidden) { ov.hidden = true; return null; }
    const r = target.getBoundingClientRect(), sr = st.getBoundingClientRect();
    ov.hidden = false;
    ov.style.left = (r.left - sr.left) + 'px'; ov.style.top = (r.top - sr.top) + 'px';
    ov.style.width = r.width + 'px'; ov.style.height = r.height + 'px';
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = Math.max(1, Math.round(r.width * dpr)), H = Math.max(1, Math.round(r.height * dpr));
    if (ov.width !== W || ov.height !== H) { ov.width = W; ov.height = H; }
    const g = ov.getContext('2d');
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, W, H);
    return { g, W, H, dpr, r };
}

function drawLiveOverlay() {
    const L = S.live;
    const o = placeOverlay($('nsLive'));
    if (!o || !L.grab) return;
    const { g, W, H, dpr } = o;
    if (L.quad && !S.raw) {
        g.lineWidth = 2.5 * dpr;
        g.strokeStyle = 'rgba(56,245,138,.95)';
        g.setLineDash([]);
        g.beginPath();
        L.quad.forEach((p, i) => {
            const q = liveSrcToOut(p.x / L.grab.w, p.y / L.grab.h);
            if (i) g.lineTo(q.x * W, q.y * H); else g.moveTo(q.x * W, q.y * H);
        });
        g.closePath(); g.stroke();
    } else if (!S.raw) {
        g.lineWidth = 1.5 * dpr;
        g.strokeStyle = 'rgba(255,255,255,.55)';
        g.setLineDash([8 * dpr, 6 * dpr]);
        g.strokeRect(W * 0.08, H * 0.08, W * 0.84, H * 0.84);
    }
}

/* ─────────────────────────────── capture ─────────────────────────────── */

function withTimeout(p, ms) {
    return Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))]);
}

/** The sharpest still this device will hand over: the sensor through
 *  ImageCapture (Android Chrome: 12–50 Mpx instead of the 2 Mpx stream),
 *  otherwise the current video frame. */
async function takeStill() {
    const L = S.live;
    const v = $('nsVideo');
    if (L.track && typeof window.ImageCapture === 'function' && !window.__NS_NO_IMAGECAPTURE) {
        try {
            const ic = new window.ImageCapture(L.track);
            const pc = await withTimeout(ic.getPhotoCapabilities(), 1500).catch(() => null);
            const set = {};
            if (pc && pc.imageWidth && pc.imageWidth.max) { set.imageWidth = pc.imageWidth.max; set.imageHeight = pc.imageHeight.max; }
            if (pc && pc.fillLightMode && pc.fillLightMode.includes && pc.fillLightMode.includes('off')) set.fillLightMode = 'off';
            const b = await withTimeout(ic.takePhoto(set), 6000);
            if (b && b.size > 1000) {
                const bmp = await decodeBlob(b);
                const d = dimsOf(bmp);
                if (d.w >= v.videoWidth && d.h >= Math.min(v.videoHeight, v.videoWidth) * 0.5) return { blob: b, bmp, via: 'imagecapture' };
            }
        } catch (e) { diag({ issue: 'takephoto_failed', msg: String((e && e.message) || e).slice(0, 60) }); }
    }
    const c = canvasOf(v.videoWidth, v.videoHeight);
    if (!window.__drawCameraFrame(c.getContext('2d'), v, 0, 0)) return null;   // an interrupted camera
    const blob = await toBlob(c, 'image/jpeg', 0.95);
    return { blob, bmp: c, via: 'video' };
}

async function capture() {
    const L = S.live;
    if (!L || !L.stream || S.busy) return;
    S.busy = true;
    const sh = $('nsShutter'); if (sh) sh.disabled = true;
    busy(T('capturing'));
    try {
        const still = await takeStill();
        if (!still) { busy(null); toast(window.__cameraNoFrame(), 'warning'); return; }
        const snap = {
            rot: L.rot, flip: L.flip,
            wb: L.wb === 'base' && L.base ? 'base' : 'auto', base: L.base,
            adjust: clone(S.liveAdj),
        };
        const recs = await addSource(still.blob, still.bmp, { from: 'camera', snap, via: still.via });
        stage('captured', { via: still.via, film: S.film });
        if (S.keepShooting) {
            toast(T('frame_added', { n: S.frames.length }), 'success');
            busy(null);
        } else {
            stopCamera();
            busy(null);
            if (recs[0]) openFrame(recs[0]);
        }
    } catch (e) {
        busy(null);
        diag({ issue: 'capture_failed', msg: String((e && e.message) || e).slice(0, 80) });
        toast(T('err_capture'), 'error');
    } finally {
        S.busy = false;
        if (sh) sh.disabled = false;
    }
}

/* ──────────────────────────── sources & frames ───────────────────────── */

/**
 * Store a source and cut its frames. Camera: the frame nearest the middle.
 * Files: every frame found (a strip scan → six frames), or the whole picture.
 */
async function addSource(blob, bmp, opt) {
    const d0 = dimsOf(bmp);
    // Cap what we keep; re-encode only when we had to shrink or the browser
    // cannot re-open the original (TIFF was decoded by us).
    let keepBlob = blob, dims = d0, bitmap = bmp;
    if (Math.max(d0.w, d0.h) > SRC_MAX || d0.w * d0.h > AREA_MAX || opt.reencode) {
        const s = scaled(bmp, SRC_MAX, true);
        keepBlob = await toBlob(s.canvas, 'image/jpeg', 0.96);
        dims = { w: s.w, h: s.h };
        bitmap = s.canvas;
    }
    const srcId = uid();
    const src = { id: srcId, blob: keepBlob, w: dims.w, h: dims.h, name: opt.name || '', created: Date.now() };
    S.srcCache.set(srcId, { bmp: bitmap, w: dims.w, h: dims.h });
    try { await DB.putSource(src); } catch (e) { storageFailed(e); }

    const an = scaled(bitmap, ANALYZE);
    let det = null;
    try { det = FR.detectFrames(an.data, an.w, an.h, S.film); } catch (_) { det = null; }
    let quads = det && det.frames.length ? det.frames : [];
    if (opt.from === 'camera' && quads.length > 1) quads = [FR.centralFrame(quads, an.w, an.h)];
    const found = quads.length > 0;
    if (!found) quads = [FR.fullQuad(an.w, an.h)];
    const k = dims.w / an.w;
    const snap = opt.snap || {};
    const created = Date.now();
    const recs = [];
    for (let i = 0; i < quads.length; i++) {
        const rec = {
            id: uid(), srcId, idx: i, created,
            n: S.frames.length + 1,
            film: S.film,
            quad: FR.scaleQuad(quads[i], k),
            auto: found,
            rot: snap.rot || 0, flip: !!snap.flip,
            wb: snap.wb || 'auto',
            base: snap.base || null,
            detBase: det && det.base ? det.base.slice() : null,
            adjust: snap.adjust ? clone(snap.adjust) : C.defaultAdjust(),
            dust: false,
            levels: null,
            name: opt.name || '',
            limitKey: opt.keyBase ? opt.keyBase + '#' + i : 'nscam:' + uid(),
            paid: false,
            from: opt.from,
        };
        // A base read off this still's own rebate beats one sampled from the
        // video stream, whose processing differs from the photo's.
        if (rec.wb === 'base' && rec.detBase) rec.base = rec.detBase.slice();
        S.frames.push(rec);
        recs.push(rec);
    }
    // Thumbnails and first levels, without opening the editor.
    for (const rec of recs) {
        try { await refreshThumb(rec); } catch (_) {}
        persist(rec);
    }
    renderStrip();
    funnel('frames_added', { from: opt.from, n: recs.length, detected: found ? 1 : 0, film: S.film });
    return recs;
}

function storageFailed(e) {
    if (S.warnedStorage) return;
    S.warnedStorage = true;
    const quota = e && (e.name === 'QuotaExceededError' || /quota/i.test(String(e.message || '')));
    toast(T(quota ? 'err_storage_full' : 'err_storage'), 'warning');
    diag({ issue: 'storage_failed', name: String((e && e.name) || '?') });
}

function persist(rec) {
    const copy = Object.assign({}, rec);
    delete copy.thumbUrl;
    DB.putFrame(copy).catch(storageFailed);
}

async function sourceOf(rec) {
    let s = S.srcCache.get(rec.srcId);
    if (s) return s;
    const row = await DB.getSource(rec.srcId);
    if (!row || !row.blob) throw new Error('source-missing');
    const bmp = await decodeBlob(row.blob);
    const d = dimsOf(bmp);
    s = { bmp, w: d.w, h: d.h };
    // keep a few decoded sources, not the whole roll
    if (S.srcCache.size > 4) S.srcCache.delete(S.srcCache.keys().next().value);
    S.srcCache.set(rec.srcId, s);
    return s;
}

/** Warp at a preview size from a downscaled copy of the source. */
async function previewOf(rec, maxOut) {
    const src = await sourceOf(rec);
    const p = scaled(src.bmp, PREV_SRC);
    const q = FR.scaleQuad(rec.quad, p.w / src.w);
    const wp = FR.warp(p.data, p.w, p.h, q, { rot: rec.rot, flip: rec.flip, maxLong: maxOut });
    return { p, q, wp };
}

async function refreshThumb(rec) {
    const { wp } = await previewOf(rec, 700);
    if (!rec.levels) rec.levels = levelsOf(wp.data, wp.w, wp.h, rec.film);
    const lut = C.buildLut(paramsOf(rec));
    const out = new Uint8ClampedArray(wp.data.length);
    C.applyLut(wp.data, out, lut, rec.film === 'bw', rec.adjust.sat);
    const k = Math.min(1, 220 / Math.max(wp.w, wp.h));
    const full = canvasOf(wp.w, wp.h);
    full.getContext('2d').putImageData(new ImageData(out, wp.w, wp.h), 0, 0);
    const t = canvasOf(Math.max(1, Math.round(wp.w * k)), Math.max(1, Math.round(wp.h * k)));
    const tg = t.getContext('2d'); tg.imageSmoothingQuality = 'high';
    tg.drawImage(full, 0, 0, t.width, t.height);
    rec.thumb = await toBlob(t, 'image/jpeg', 0.82);
    if (rec.thumbUrl) URL.revokeObjectURL(rec.thumbUrl);
    rec.thumbUrl = URL.createObjectURL(rec.thumb);
}

/* ─────────────────────────────── the editor ──────────────────────────── */

async function openFrame(rec) {
    stopCamera();
    goView('edit');
    busy(T('opening'));
    try {
        const src = await sourceOf(rec);
        const p = scaled(src.bmp, PREV_SRC);
        S.cur = { rec, src, prev: p, mode: 'look', warped: null, lut: null };
        S.fmt = pref('fmt', 'jpg') === 'png' ? 'png' : 'jpg';
        syncEditControls();
        refreshEdit(!rec.levels);
        stage('frame_opened', { film: rec.film });
    } catch (e) {
        diag({ issue: 'open_failed', msg: String((e && e.message) || e).slice(0, 60) });
        toast(T('err_open'), 'error');
        goView(S.frames.length ? 'edit-empty' : 'start');
    } finally {
        busy(null);
    }
    renderStrip();
}

function curQuadPrev() {
    const c = S.cur;
    return FR.scaleQuad(c.rec.quad, c.prev.w / c.src.w);
}

/** Re-warp (geometry changed) and redraw. */
function refreshEdit(relevel) {
    const c = S.cur;
    if (!c) return;
    const rec = c.rec;
    c.warped = FR.warp(c.prev.data, c.prev.w, c.prev.h, curQuadPrev(), { rot: rec.rot, flip: rec.flip, maxLong: PREV_OUT });
    if (relevel || !rec.levels) rec.levels = levelsOf(c.warped.data, c.warped.w, c.warped.h, rec.film);
    redraw();
    scheduleThumb();
}

let redrawRaf = 0;
function redraw() {
    if (redrawRaf) return;
    redrawRaf = requestAnimationFrame(() => { redrawRaf = 0; drawEdit(); });
}

function drawEdit() {
    const c = S.cur;
    if (!c || S.view !== 'edit') return;
    const rec = c.rec;
    const cv = $('nsView');
    c.lut = C.buildLut(paramsOf(rec));
    const showFull = c.mode === 'crop' || c.mode === 'pickBase';
    const srcImg = showFull ? { data: c.prev.data, w: c.prev.w, h: c.prev.h } : c.warped;
    if (cv.width !== srcImg.w || cv.height !== srcImg.h) { cv.width = srcImg.w; cv.height = srcImg.h; }
    const g = cv.getContext('2d');
    const out = new Uint8ClampedArray(srcImg.data.length);
    if (S.raw || c.mode === 'pickBase') out.set(srcImg.data);
    else C.applyLut(srcImg.data, out, c.lut, rec.film === 'bw', rec.adjust.sat);
    if (!S.raw && !showFull && rec.dust) removeDust(out, srcImg.w, srcImg.h, { bright: rec.film !== 'slide' });
    g.putImageData(new ImageData(out, srcImg.w, srcImg.h), 0, 0);
    cv.hidden = false;
    drawEditOverlay();
    const res = FR.outSize(rec.quad, rec.rot);
    $('nsRes').textContent = T('resolution', { w: res.w, h: res.h, mp: ((res.w * res.h) / 1e6).toFixed(1) });
}

function drawEditOverlay() {
    const c = S.cur;
    if (!c) return;
    const o = placeOverlay($('nsView'));
    if (!o) return;
    const { g, W, H, dpr } = o;
    if (c.mode !== 'crop') return;
    const q = curQuadPrev();
    const sx = W / c.prev.w, sy = H / c.prev.h;
    g.fillStyle = 'rgba(0,0,0,.45)';
    g.beginPath();
    g.rect(0, 0, W, H);
    g.moveTo(q[0].x * sx, q[0].y * sy);
    for (let i = 3; i >= 0; i--) g.lineTo(q[i].x * sx, q[i].y * sy);
    g.closePath();
    g.fill('evenodd');
    g.strokeStyle = '#38f58a'; g.lineWidth = 2 * dpr;
    g.beginPath();
    q.forEach((p, i) => (i ? g.lineTo(p.x * sx, p.y * sy) : g.moveTo(p.x * sx, p.y * sy)));
    g.closePath(); g.stroke();
    q.forEach((p, i) => {
        g.beginPath();
        g.arc(p.x * sx, p.y * sy, (c.drag === i ? 13 : 10) * dpr, 0, Math.PI * 2);
        g.fillStyle = '#fff'; g.fill();
        g.lineWidth = 3 * dpr; g.strokeStyle = '#16a34a'; g.stroke();
    });
}

function setEditMode(m) {
    const c = S.cur;
    if (!c) return;
    c.mode = m;
    $('nsCropBtn').classList.toggle('on', m === 'crop');
    $('nsCropBtn').setAttribute('aria-pressed', m === 'crop' ? 'true' : 'false');
    $('nsOver').classList.toggle('is-crop', m === 'crop');
    if (m === 'crop') hud(T('crop_hint'));
    else if (m === 'pickBase') hud(T('tap_base_edit'), 'warn');
    else if (m === 'pickGrey') hud(T('tap_grey'), 'warn');
    else hud(null);
    if (m === 'look') refreshEdit(false); else redraw();
}

function viewPoint(ev) {
    const cv = $('nsView');
    const r = cv.getBoundingClientRect();
    return { nx: (ev.clientX - r.left) / r.width, ny: (ev.clientY - r.top) / r.height, r };
}

function onViewPointer(ev, type) {
    const c = S.cur;
    if (!c || S.view !== 'edit') return;
    const { nx, ny, r } = viewPoint(ev);
    if (c.mode === 'crop') {
        const q = curQuadPrev();
        const px = nx * c.prev.w, py = ny * c.prev.h;
        if (type === 'down') {
            let best = -1, bd = Infinity;
            q.forEach((p, i) => { const d = Math.hypot((p.x - px) * r.width / c.prev.w, (p.y - py) * r.height / c.prev.h); if (d < bd) { bd = d; best = i; } });
            if (bd < 40) { c.drag = best; ev.preventDefault(); try { ev.target.setPointerCapture(ev.pointerId); } catch (_) {} }
        } else if (type === 'move' && c.drag != null && c.drag >= 0) {
            ev.preventDefault();
            const k = c.src.w / c.prev.w;
            c.rec.quad[c.drag] = { x: Math.max(0, Math.min(c.src.w, px * k)), y: Math.max(0, Math.min(c.src.h, py * k)) };
            c.rec.auto = false;
            drawEditOverlay();
        } else if (type === 'up' && c.drag != null && c.drag >= 0) {
            c.drag = null;
            funnel('corner_moved', {});
            persist(c.rec);
            drawEditOverlay();
        }
        return;
    }
    if (type !== 'down') return;
    if (c.mode === 'pickBase') {
        const rgb = C.sampleAt(c.prev.data, c.prev.w, c.prev.h, nx * c.prev.w, ny * c.prev.h, 3);
        c.rec.base = rgb; c.rec.wb = 'base';
        funnel('base_tapped', { where: 'edit' });
        toast(T('base_set'), 'success');
        persist(c.rec);
        setEditMode('look');
        syncEditControls();
        return;
    }
    if (c.mode === 'pickGrey') {
        const p = FR.outToSrc(curQuadPrev(), c.rec.rot, c.rec.flip, nx, ny);
        const rgb = C.sampleAt(c.prev.data, c.prev.w, c.prev.h, p.x, p.y, 3);
        const gains = C.neutralGains(paramsOf(c.rec), rgb);
        c.rec.adjust.neutral = gains; c.rec.adjust.temp = 0; c.rec.adjust.tint = 0;
        funnel('grey_tapped', {});
        persist(c.rec);
        setEditMode('look');
        syncEditControls();
    }
}

/* ──────────────────────────── edit controls ──────────────────────────── */

const SLIDERS = [
    ['nsExposure', 'exposure', (v) => (v > 0 ? '+' : '') + (+v).toFixed(1)],
    ['nsBright', 'bright', (v) => Math.round(v * 100)],
    ['nsContrast', 'contrast', (v) => Math.round(v * 100)],
    ['nsTemp', 'temp', (v) => Math.round(v * 100)],
    ['nsTint', 'tint', (v) => Math.round(v * 100)],
    ['nsSat', 'sat', (v) => Math.round(v * 100) + '%'],
];

/** The adjustments the controls currently edit: the frame in the editor, the
 *  live view otherwise. */
function adjTarget() { return S.view === 'edit' && S.cur ? S.cur.rec.adjust : S.liveAdj; }

function syncSliders() {
    const A = adjTarget();
    for (const [id, key, fmt] of SLIDERS) {
        const el = $(id); if (!el) continue;
        el.value = A[key];
        const out = $(id + 'Out'); if (out) out.textContent = fmt(A[key]);
    }
    const ch = S.lvCh || 'm';
    const s = (A.lv && A.lv[ch]) || { b: 0, m: 0, w: 1 };
    $('nsLvB').value = s.b; $('nsLvM').value = s.m; $('nsLvW').value = s.w;
    $('nsLvBOut').textContent = Math.round(s.b * 255);
    $('nsLvMOut').textContent = (Math.pow(2, s.m)).toFixed(2);
    $('nsLvWOut').textContent = Math.round(s.w * 255);
    document.querySelectorAll('#nsLvCh [data-ch]').forEach((b) => b.classList.toggle('on', b.dataset.ch === ch));
}

function syncEditControls() {
    const c = S.cur;
    if (c) {
        const rec = c.rec;
        document.querySelectorAll('#nsWb [data-wb]').forEach((b) => {
            const on = b.dataset.wb === (rec.adjust.neutral ? 'grey' : rec.wb);
            b.classList.toggle('on', on);
            b.hidden = b.dataset.wb === 'base' && rec.film === 'slide';
        });
        $('nsDust').checked = !!rec.dust;
        document.querySelectorAll('#nsFmt [data-fmt]').forEach((b) => b.classList.toggle('on', b.dataset.fmt === S.fmt));
        $('nsSave').textContent = T(rec.paid ? 'save_again' : 'save', { fmt: S.fmt.toUpperCase() });
        $('nsEditTitle').textContent = T('frame_n', { n: rec.n });
    }
    syncSliders();
    syncFilmButtons();
}

function syncFilmButtons() {
    const f = S.cur && S.view === 'edit' ? S.cur.rec.film : S.film;
    document.querySelectorAll('#nsFilm [data-film]').forEach((b) => {
        const on = b.dataset.film === f;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
}

function onAdjust() {
    if (S.view === 'edit' && S.cur) { redraw(); persistSoon(S.cur.rec); scheduleThumb(); }
    else if (S.live) {
        S.live.lut = C.buildLut({ mode: S.film, wb: S.live.wb === 'base' && S.live.base && neg() ? 'base' : 'auto', base: S.live.base, levels: S.live.levels, adjust: S.liveAdj });
        if (S.live.renderer) S.live.renderer.setLut(S.live.lut);
        setPref('adj', S.liveAdj);
    }
}

let persistT = 0;
function persistSoon(rec) { clearTimeout(persistT); persistT = setTimeout(() => persist(rec), 400); }
let thumbT = 0;
function scheduleThumb() {
    clearTimeout(thumbT);
    const rec = S.cur && S.cur.rec;
    if (!rec) return;
    thumbT = setTimeout(() => { refreshThumb(rec).then(() => { persist(rec); renderStrip(); }).catch(() => {}); }, 900);
}

/* ─────────────────────────────── saving ──────────────────────────────── */

function fileName(rec, fmt, used) {
    const ON = window.__outputName;
    const d = new Date(rec.created);
    const date = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    const base = rec.name ? rec.name.replace(/\.[a-z0-9]{2,5}$/i, '') : T('file_prefix') + '-' + date;
    const parts = [base, String(rec.n).padStart(2, '0')];
    let name = ON && ON.join ? ON.join(parts, fmt) : parts.join('-').replace(/[\\/:*?"<>|]/g, '-') + '.' + fmt;
    if (!name) name = 'film-' + String(rec.n).padStart(2, '0') + '.' + fmt;
    if (used && ON && ON.distinct) { name = ON.distinct(name, used); used.add(name); }
    return name;
}

/** The frame at full resolution, as a file. */
async function renderFull(rec, fmt) {
    const src = await sourceOf(rec);
    const q = rec.quad;
    const xs = q.map((p) => p.x), ys = q.map((p) => p.y);
    const bx = Math.max(0, Math.floor(Math.min(...xs))), by = Math.max(0, Math.floor(Math.min(...ys)));
    const bw = Math.min(src.w, Math.ceil(Math.max(...xs))) - bx, bh = Math.min(src.h, Math.ceil(Math.max(...ys))) - by;
    const cut = canvasOf(Math.max(1, bw), Math.max(1, bh));
    const cg = cut.getContext('2d', { willReadFrequently: true });
    cg.drawImage(src.bmp, bx, by, bw, bh, 0, 0, bw, bh);
    const data = cg.getImageData(0, 0, cut.width, cut.height).data;
    await nextFrame();
    const lq = q.map((p) => ({ x: p.x - bx, y: p.y - by }));
    const wp = FR.warp(data, cut.width, cut.height, lq, { rot: rec.rot, flip: rec.flip });
    if (!rec.levels) {
        const pv = await previewOf(rec, 700);
        rec.levels = levelsOf(pv.wp.data, pv.wp.w, pv.wp.h, rec.film);
    }
    const lut = C.buildLut(paramsOf(rec));
    C.applyLut(wp.data, wp.data, lut, rec.film === 'bw', rec.adjust.sat);
    if (rec.dust) { await nextFrame(); removeDust(wp.data, wp.w, wp.h, { bright: rec.film !== 'slide' }); }
    const out = canvasOf(wp.w, wp.h);
    out.getContext('2d').putImageData(new ImageData(wp.data, wp.w, wp.h), 0, 0);
    return toBlob(out, fmt === 'png' ? 'image/png' : 'image/jpeg', 0.95);
}

async function saveCurrent() {
    const c = S.cur;
    if (!c || S.busy) return;
    const rec = c.rec;
    const L = TL();
    // One unit per FRAME: the key was stamped when the frame was made, so a
    // second format or another edit of the same frame is the same unit.
    let run = null;
    if (!rec.paid && L) {
        run = L.begin(SLUG, [rec.limitKey]);
        if (!run) return;     // the shared note is on screen
    }
    S.busy = true;
    const btn = $('nsSave'); btn.disabled = true;
    busy(T('saving'));
    try {
        const fmt = S.fmt;
        const blob = await renderFull(rec, fmt);
        const name = fileName(rec, fmt);
        if (!window.__downloadFile) throw new Error('no-download');
        await window.__downloadFile(blob, name);
        if (run) run.commit();
        const first = !rec.paid;
        rec.paid = true;
        persist(rec);
        stage('frame_saved', { fmt, film: rec.film });
        complete({ film: rec.film, fmt, from: rec.from, frames: 1, auto_crop: rec.auto ? 1 : 0, dust: rec.dust ? 1 : 0, repeat: first ? 0 : 1 });
        showNext(rec.film);
        renderStrip();
    } catch (e) {
        if (run) run.abort();
        diag({ issue: 'save_failed', msg: String((e && e.message) || e).slice(0, 80) });
        toast(T('err_save'), 'error');
    } finally {
        S.busy = false; btn.disabled = false; busy(null);
        syncEditControls();
    }
}

async function saveAll() {
    if (S.busy || !S.frames.length) return;
    if (S.frames.length === 1) {
        if (S.cur && S.cur.rec === S.frames[0]) return saveCurrent();
        await openFrame(S.frames[0]);
        return saveCurrent();
    }
    const L = TL();
    const keys = S.frames.filter((r) => !r.paid).map((r) => r.limitKey);
    let run = null;
    if (keys.length && L) {
        run = L.begin(SLUG, keys);
        if (!run) return;
    }
    const BD = window.__batchDeliver;
    const fmt = S.fmt;
    S.busy = true;
    let batch = null;
    try {
        if (!BD) throw new Error('no-batch');
        batch = await BD.openBatch({ zipName: fileName({ created: Date.now(), n: 0, name: '' }, 'zip').replace(/-00\.zip$/, '.zip'), slug: SLUG, total: S.frames.length });
        if (!batch) { if (run) run.abort(); return; }
        const used = new Set();
        let i = 0;
        for (const rec of S.frames) {
            i++;
            busy(T('saving_n', { i, n: S.frames.length }));
            const blob = await renderFull(rec, fmt);
            await batch.write(fileName(rec, fmt, used), blob);
        }
        const res = await batch.finish();
        // Only a delivered batch is a unit spent: an archive refused as too big
        // or a folder write that failed midway hands nothing complete back.
        if (!res || (res.via !== 'folder' && res.via !== 'zip')) {
            if (run) run.abort();
            if (res && res.via === 'folder_fail') toast(T('err_save'), 'error');
            return;
        }
        if (run) run.commit();
        for (const rec of S.frames) { rec.paid = true; persist(rec); }
        stage('frame_saved', { fmt, batch: S.frames.length });
        complete({ film: S.film, fmt, frames: S.frames.length, via: batch.mode || 'batch' });
        showNext(S.frames[0].film);
        renderStrip();
        if (S.cur) syncEditControls();
    } catch (e) {
        if (run) run.abort();
        const code = e && (e.code || e.message);
        if (code !== 'aborted' && !(e && e.name === 'AbortError')) {
            diag({ issue: 'batch_failed', msg: String(code || e).slice(0, 80) });
            toast(T('err_save'), 'error');
        }
    } finally {
        S.busy = false; busy(null);
    }
}

function showNext(film) {
    const n = $('nsNext');
    if (!n) return;
    n.hidden = false;
    n.querySelectorAll('[data-next]').forEach((a) => {
        a.classList.toggle('is-lead', a.dataset.next === (film === 'bw' ? 'photo-colorize' : 'photo-restore'));
    });
}

/* ─────────────────────────────── the strip ───────────────────────────── */

function renderStrip() {
    const wrap = $('nsStrip'), list = $('nsStripList');
    if (!wrap || !list) return;
    wrap.hidden = !S.frames.length;
    $('nsCount').textContent = T('frames_n', { n: S.frames.length });
    const saveAllBtn = $('nsSaveAll');
    saveAllBtn.textContent = T('save_all', { n: S.frames.length });
    list.innerHTML = '';
    S.frames.forEach((rec, i) => {
        rec.n = i + 1;
        const item = document.createElement('div');
        item.className = 'ns-thumb' + (S.cur && S.cur.rec === rec && S.view === 'edit' ? ' is-on' : '');
        const open = document.createElement('button');
        open.type = 'button';
        open.className = 'ns-thumb-open';
        open.dataset.open = rec.id;
        open.setAttribute('aria-label', T('frame_n', { n: rec.n }));
        if (rec.thumbUrl) { const im = document.createElement('img'); im.src = rec.thumbUrl; im.alt = ''; open.appendChild(im); }
        const num = document.createElement('span');
        num.className = 'ns-thumb-n';
        num.textContent = rec.n + (rec.paid ? ' ✓' : '');
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'ns-thumb-x';
        del.dataset.del = rec.id;
        del.setAttribute('aria-label', T('delete_frame', { n: rec.n }));
        del.textContent = '×';
        item.append(open, num, del);
        list.appendChild(item);
    });
}

function deleteFrame(id, btn) {
    // Two taps: a captured frame is a photograph that took setting up again.
    if (!btn.classList.contains('is-armed')) {
        btn.classList.add('is-armed');
        btn.textContent = T('delete_q');
        setTimeout(() => { btn.classList.remove('is-armed'); btn.textContent = '×'; }, 2600);
        return;
    }
    const i = S.frames.findIndex((r) => r.id === id);
    if (i < 0) return;
    const [rec] = S.frames.splice(i, 1);
    if (rec.thumbUrl) URL.revokeObjectURL(rec.thumbUrl);
    DB.delFrame(rec.id).catch(() => {});
    if (!S.frames.some((r) => r.srcId === rec.srcId)) { DB.delSource(rec.srcId).catch(() => {}); S.srcCache.delete(rec.srcId); }
    if (S.cur && S.cur.rec === rec) {
        S.cur = null;
        if (S.frames.length) openFrame(S.frames[Math.min(i, S.frames.length - 1)]);
        else goView('start');
    }
    renderStrip();
}

async function restore() {
    let rows = [];
    try { rows = await DB.allFrames(); } catch (_) { rows = []; }
    if (!rows || !rows.length) return;
    for (const r of rows) {
        if (r.thumb) r.thumbUrl = URL.createObjectURL(r.thumb);
        if (!r.adjust) r.adjust = C.defaultAdjust();
        S.frames.push(r);
    }
    renderStrip();
    funnel('restored', { n: rows.length });
}

/* ─────────────────────────────── files ───────────────────────────────── */

let utifP = null;
function loadScript(src, fb) {
    return new Promise((resolve) => {
        const s = document.createElement('script');
        let tried = false;
        s.src = src;
        s.onload = () => resolve(true);
        s.onerror = () => { if (fb && !tried) { tried = true; s.src = fb; } else resolve(false); };
        document.head.appendChild(s);
    });
}
function loadUtif() {
    if (window.UTIF) return Promise.resolve(true);
    if (!utifP) utifP = loadScript(CFG.pako, CFG.pakoFb).then(() => loadScript(CFG.utif, CFG.utifFb)).then(() => !!window.UTIF);
    return utifP;
}
async function isTiff(file) {
    try {
        const h = new Uint8Array(await file.slice(0, 4).arrayBuffer());
        return (h[0] === 0x49 && h[1] === 0x49 && h[2] === 0x2A && h[3] === 0) || (h[0] === 0x4D && h[1] === 0x4D && h[2] === 0 && h[3] === 0x2A);
    } catch (_) { return false; }
}
/** Flatbed scans of film are very often TIFF, and no browser opens one. */
async function decodeTiff(file) {
    if (!(await loadUtif())) throw new Error('tiff-lib');
    const ab = await file.arrayBuffer();
    const ifds = window.UTIF.decode(ab);
    if (!ifds || !ifds.length) throw new Error('tiff');
    let ifd = ifds[0];
    for (const x of ifds) if (((x.width | 0) * (x.height | 0)) > ((ifd.width | 0) * (ifd.height | 0))) ifd = x;
    window.UTIF.decodeImage(ab, ifd);
    const rgba = window.UTIF.toRGBA8(ifd);
    const w = ifd.width | 0, h = ifd.height | 0;
    if (!w || !h || !rgba || rgba.length < w * h * 4) throw new Error('tiff');
    const c = canvasOf(w, h);
    c.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(rgba.buffer.slice(0, w * h * 4)), w, h), 0, 0);
    return c;
}

async function takeFiles(files) {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length || S.busy) return;
    funnel('input', { method: 'upload', count: list.length });
    stopCamera();
    S.busy = true;
    let made = [];
    try {
        for (let i = 0; i < list.length; i++) {
            const f = list[i];
            busy(T('reading', { i: i + 1, n: list.length }));
            await nextFrame();
            let bmp, reencode = false;
            try {
                if (await isTiff(f)) { bmp = await decodeTiff(f); reencode = true; }
                else bmp = await decodeBlob(f);
            } catch (e) {
                diag({ issue: 'decode_failed', type: String(f.type || '').slice(0, 30), msg: String((e && e.message) || e).slice(0, 40) });
                toast(T('err_decode', { name: f.name || '' }), 'error');
                continue;
            }
            const L = TL();
            const keyBase = L && L.fileKey ? 'nsfile:' + L.fileKey(f) : 'nsfile:' + (f.name || '') + '|' + f.size + '|' + f.lastModified;
            const recs = await addSource(f, bmp, { from: 'file', name: f.name || '', keyBase, reencode });
            if (recs.length > 1) toast(T('found_n', { n: recs.length, name: f.name || '' }), 'success');
            made = made.concat(recs);
        }
    } finally {
        S.busy = false;
        busy(null);
    }
    if (made.length) {
        stage('file_loaded', { frames: made.length });
        openFrame(made[0]);
    }
}

/* ─────────────────────────────── views ───────────────────────────────── */

function goView(v) {
    S.view = v === 'edit-empty' ? 'start' : v;
    const work = $('nsWork');
    show($('nsStart'), S.view === 'start');
    show(work, S.view !== 'start');
    show($('nsLiveCtl'), S.view === 'live');
    show($('nsShootBar'), S.view === 'live');
    show($('nsEditCtl'), S.view === 'edit');
    show($('nsLive'), false);
    show($('nsView'), S.view === 'edit' && !!S.cur);
    show($('nsHold'), S.view !== 'start');
    $('nsOver').hidden = true;
    hideCamFail();
    hud(null);
    if (S.view !== 'live') stopCamera();
    work.dataset.view = S.view;
    syncEditControls();
    if (S.view === 'start') { S.cur = null; renderStrip(); }
    // the adjustment panel serves both the live view and the editor
    const adjPanel = $('nsAdjPanel');
    const liveSlot = $('nsLiveAdjSlot'), editSlot = $('nsEditAdjSlot');
    if (adjPanel && liveSlot && editSlot) {
        if (S.view === 'live' && adjPanel.parentNode !== liveSlot) liveSlot.appendChild(adjPanel);
        if (S.view === 'edit' && adjPanel.parentNode !== editSlot) editSlot.appendChild(adjPanel);
    }
    show($('nsWbRow'), S.view === 'edit');
    show($('nsLevels'), S.view === 'edit');
}

function syncLiveControls() {
    const L = S.live;
    if (!L) return;
    $('nsFlipLive').classList.toggle('on', !!L.flip);
    $('nsFlipLive').setAttribute('aria-pressed', L.flip ? 'true' : 'false');
    $('nsLock').classList.toggle('on', !!L.lock);
    $('nsLock').setAttribute('aria-pressed', L.lock ? 'true' : 'false');
    syncLiveWb();
    $('nsKeep').checked = !!S.keepShooting;
}
function syncLiveWb() {
    const L = S.live;
    const b = $('nsBaseLive');
    if (!L || !b) return;
    b.hidden = S.film === 'slide';
    b.classList.toggle('on', L.wb === 'base' || L.pick === 'base');
    b.textContent = L.wb === 'base' ? T('base_on') : T('tap_base');
}

/* ─────────────────────────────── the light ───────────────────────────── */

function openWhite() {
    const p = $('nsWhitePanel');
    p.hidden = false;
    document.body.classList.add('ns-lit');
    const hint = $('nsWhiteHint');
    hint.classList.add('show');
    clearTimeout(S.whiteT);
    S.whiteT = setTimeout(() => hint.classList.remove('show'), 3500);
    try { window.__wakeLock && window.__wakeLock.hold('negative-scanner-light'); } catch (_) {}
    if (window.__safeFullscreen) window.__safeFullscreen(p);
    funnel('white_screen', {});
}
function closeWhite() {
    const p = $('nsWhitePanel');
    if (p.hidden) return;
    p.hidden = true;
    document.body.classList.remove('ns-lit');
    try { window.__wakeLock && window.__wakeLock.releaseHold('negative-scanner-light'); } catch (_) {}
    try { if (document.fullscreenElement === p && document.exitFullscreen) document.exitFullscreen().catch(() => {}); } catch (_) {}
}

function drawQr() {
    const el = $('nsQr');
    if (!el || el.dataset.done) return;
    if (typeof window.qrcode !== 'function') { setTimeout(drawQr, 400); return; }
    try {
        const qr = window.qrcode(0, 'M');
        qr.addData(CFG.whiteUrl);
        qr.make();
        el.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
        const svg = el.querySelector('svg');
        if (svg) { svg.setAttribute('role', 'img'); svg.setAttribute('aria-label', T('qr_label')); }
        el.dataset.done = '1';
    } catch (_) {}
}

/* ─────────────────────────────── wiring ──────────────────────────────── */

function bind() {
    document.querySelectorAll('#nsFilm [data-film]').forEach((b) => b.addEventListener('click', () => setFilm(b.dataset.film, 'user')));
    $('nsCamOpen').addEventListener('click', openCamera);
    $('nsShutter').addEventListener('click', capture);
    $('nsClose').addEventListener('click', () => { closeWhite(); goView('start'); });
    $('nsFs').addEventListener('click', () => {
        const st = $('nsStage');
        if (document.fullscreenElement) { try { document.exitFullscreen().catch(() => {}); } catch (_) {} return; }
        if (window.__safeFullscreen) window.__safeFullscreen(st);
    });
    const hold = $('nsHold');
    const on = (ev) => { ev.preventDefault(); S.raw = true; hold.classList.add('on'); if (S.view === 'edit') redraw(); };
    const off = () => { if (!S.raw) return; S.raw = false; hold.classList.remove('on'); if (S.view === 'edit') redraw(); };
    hold.addEventListener('pointerdown', on);
    ['pointerup', 'pointerleave', 'pointercancel'].forEach((e) => hold.addEventListener(e, off));
    hold.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') on(e); });
    hold.addEventListener('keyup', off);

    // live controls
    $('nsRotLive').addEventListener('click', () => { const L = S.live; if (!L) return; L.rot = (L.rot + 90) % 360; setPref('rot', L.rot); });
    $('nsFlipLive').addEventListener('click', () => { const L = S.live; if (!L) return; L.flip = !L.flip; setPref('flip', L.flip); syncLiveControls(); });
    $('nsLock').addEventListener('click', () => { const L = S.live; if (!L) return; L.lock = !L.lock; syncLiveControls(); funnel('lock_colors', { on: L.lock ? 1 : 0 }); });
    $('nsBaseLive').addEventListener('click', () => {
        const L = S.live; if (!L) return;
        if (L.wb === 'base') { L.wb = 'auto'; L.base = null; L.pick = null; }
        else if (L.pick === 'base') L.pick = null;
        else L.pick = 'base';
        syncLiveWb();
        onAdjust();
    });
    $('nsKeep').addEventListener('change', (e) => { S.keepShooting = !!e.target.checked; setPref('keep', S.keepShooting); });
    $('nsSwitch').addEventListener('click', () => {
        const L = S.live; if (!L) return;
        startCamera({ facing: L.facing === 'environment' ? 'user' : 'environment' });
    });
    $('nsZoom').addEventListener('input', (e) => { $('nsZoomOut').textContent = (+e.target.value).toFixed(1) + '×'; applyCam({ zoom: +e.target.value }); });
    $('nsExp').addEventListener('input', (e) => { $('nsExpOut').textContent = (+e.target.value).toFixed(1); applyCam({ exposureCompensation: +e.target.value }); });
    $('nsCamFailPick').addEventListener('click', () => { goView('start'); pickFiles(); });
    $('nsCamFailRetry').addEventListener('click', () => startCamera());
    $('nsLive').addEventListener('pointerdown', (ev) => {
        const L = S.live; if (!L || !L.grab) return;
        const r = ev.currentTarget.getBoundingClientRect();
        const nx = (ev.clientX - r.left) / r.width, ny = (ev.clientY - r.top) / r.height;
        const s = liveOutToSrc(nx, ny);
        if (L.pick === 'base') {
            L.base = C.sampleAt(L.grab.data, L.grab.w, L.grab.h, s.x * L.grab.w, s.y * L.grab.h, 2);
            L.wb = 'base'; L.pick = null;
            funnel('base_tapped', { where: 'live' });
            toast(T('base_set'), 'success');
            syncLiveWb(); onAdjust();
            return;
        }
        // tap to focus where the track supports a point of interest
        const caps = capsOf();
        if (caps.pointsOfInterest || (caps.focusMode && caps.focusMode.includes('single-shot'))) {
            applyCam({ pointsOfInterest: [{ x: s.x, y: s.y }] });
        }
    });

    // adjustments
    for (const [id, key, fmt] of SLIDERS) {
        const el = $(id);
        el.addEventListener('input', () => {
            const A = adjTarget();
            A[key] = +el.value;
            if ((key === 'temp' || key === 'tint') && A.neutral) { A.neutral = null; syncEditControls(); }
            $(id + 'Out').textContent = fmt(A[key]);
            onAdjust();
        });
        el.addEventListener('dblclick', () => {
            const d = C.defaultAdjust();
            adjTarget()[key] = d[key];
            syncSliders(); onAdjust();
        });
    }
    $('nsReset').addEventListener('click', () => {
        const d = C.defaultAdjust();
        const A = adjTarget();
        Object.assign(A, d);
        if (S.view === 'edit' && S.cur) { S.cur.rec.wb = 'auto'; S.cur.rec.levels = null; refreshEdit(true); }
        syncEditControls(); onAdjust();
    });
    document.querySelectorAll('#nsLvCh [data-ch]').forEach((b) => b.addEventListener('click', () => { S.lvCh = b.dataset.ch; syncSliders(); }));
    const lvInput = () => {
        const A = adjTarget();
        const ch = S.lvCh || 'm';
        if (!A.lv) A.lv = C.defaultAdjust().lv;
        const s = A.lv[ch] = { b: +$('nsLvB').value, m: +$('nsLvM').value, w: +$('nsLvW').value };
        if (s.w < s.b + 0.05) { s.w = Math.min(1, s.b + 0.05); $('nsLvW').value = s.w; }
        syncSliders(); onAdjust();
    };
    ['nsLvB', 'nsLvM', 'nsLvW'].forEach((id) => $(id).addEventListener('input', lvInput));

    // editor
    document.querySelectorAll('#nsWb [data-wb]').forEach((b) => b.addEventListener('click', () => {
        const c = S.cur; if (!c) return;
        const w = b.dataset.wb;
        if (w === 'auto') { c.rec.wb = 'auto'; c.rec.adjust.neutral = null; setEditMode('look'); }
        else if (w === 'base') {
            c.rec.adjust.neutral = null;
            if (c.rec.base || c.rec.detBase) { c.rec.base = c.rec.base || c.rec.detBase; c.rec.wb = 'base'; setEditMode('look'); toast(T('base_from_rebate'), 'info'); }
            else setEditMode('pickBase');
        } else if (w === 'grey') setEditMode('pickGrey');
        persist(c.rec); syncEditControls(); redraw();
    }));
    $('nsPickBase').addEventListener('click', () => setEditMode('pickBase'));
    $('nsCropBtn').addEventListener('click', () => { if (!S.cur) return; setEditMode(S.cur.mode === 'crop' ? 'look' : 'crop'); });
    $('nsAutoCrop').addEventListener('click', async () => {
        const c = S.cur; if (!c) return;
        const an = scaled(c.src.bmp, ANALYZE);
        const det = FR.detectFrames(an.data, an.w, an.h, c.rec.film);
        if (det.frames.length) {
            // the detected frame closest to the current crop
            const cq = FR.scaleQuad(c.rec.quad, an.w / c.src.w);
            const cc = { x: cq.reduce((s, p) => s + p.x, 0) / 4, y: cq.reduce((s, p) => s + p.y, 0) / 4 };
            let best = det.frames[0], bd = Infinity;
            for (const q of det.frames) { const d = Math.hypot(q.reduce((s, p) => s + p.x, 0) / 4 - cc.x, q.reduce((s, p) => s + p.y, 0) / 4 - cc.y); if (d < bd) { bd = d; best = q; } }
            c.rec.quad = FR.scaleQuad(best, c.src.w / an.w);
            c.rec.auto = true;
            if (det.base) c.rec.detBase = det.base;
            toast(T('frame_detected'), 'success');
        } else toast(T('no_frame'), 'warning');
        persist(c.rec); setEditMode('look'); refreshEdit(true);
    });
    $('nsFullFrame').addEventListener('click', () => {
        const c = S.cur; if (!c) return;
        c.rec.quad = FR.fullQuad(c.src.w, c.src.h); c.rec.auto = false;
        persist(c.rec); setEditMode('look'); refreshEdit(true);
    });
    const rot = (d) => { const c = S.cur; if (!c) return; c.rec.rot = (c.rec.rot + d + 360) % 360; persist(c.rec); refreshEdit(false); };
    $('nsRotL').addEventListener('click', () => rot(-90));
    $('nsRotR').addEventListener('click', () => rot(90));
    $('nsFlip').addEventListener('click', () => { const c = S.cur; if (!c) return; c.rec.flip = !c.rec.flip; persist(c.rec); refreshEdit(false); });
    $('nsDust').addEventListener('change', (e) => { const c = S.cur; if (!c) return; c.rec.dust = !!e.target.checked; persist(c.rec); redraw(); funnel('dust', { on: c.rec.dust ? 1 : 0 }); });
    document.querySelectorAll('#nsFmt [data-fmt]').forEach((b) => b.addEventListener('click', () => { S.fmt = b.dataset.fmt; setPref('fmt', S.fmt); syncEditControls(); }));
    $('nsSave').addEventListener('click', saveCurrent);
    $('nsSaveAll').addEventListener('click', saveAll);
    $('nsNextShot').addEventListener('click', openCamera);
    $('nsAddFiles').addEventListener('click', pickFiles);
    $('nsClear').addEventListener('click', (e) => {
        const b = e.currentTarget;
        if (!b.classList.contains('is-armed')) {
            b.classList.add('is-armed'); b.textContent = T('clear_q');
            setTimeout(() => { b.classList.remove('is-armed'); b.textContent = T('clear'); }, 2600);
            return;
        }
        S.frames.forEach((r) => r.thumbUrl && URL.revokeObjectURL(r.thumbUrl));
        S.frames = []; S.cur = null; S.srcCache.clear();
        DB.clearAll().catch(() => {});
        b.classList.remove('is-armed'); b.textContent = T('clear');
        goView('start');
    });
    $('nsStripList').addEventListener('click', (e) => {
        const del = e.target.closest('[data-del]');
        if (del) { deleteFrame(del.dataset.del, del); return; }
        const op = e.target.closest('[data-open]');
        if (op) { const rec = S.frames.find((r) => r.id === op.dataset.open); if (rec && !S.busy) openFrame(rec); }
    });
    const view = $('nsView'), over = $('nsOver');
    [view, over].forEach((el) => {
        el.addEventListener('pointerdown', (e) => onViewPointer(e, 'down'));
        el.addEventListener('pointermove', (e) => onViewPointer(e, 'move'));
        el.addEventListener('pointerup', (e) => onViewPointer(e, 'up'));
        el.addEventListener('pointercancel', (e) => onViewPointer(e, 'up'));
    });

    // the light
    $('nsWhite').addEventListener('click', openWhite);
    const wp = $('nsWhitePanel');
    wp.addEventListener('dblclick', closeWhite);
    wp.addEventListener('click', () => { const h = $('nsWhiteHint'); h.classList.add('show'); clearTimeout(S.whiteT); S.whiteT = setTimeout(() => h.classList.remove('show'), 3000); });
    let lastTap = 0;
    wp.addEventListener('pointerup', (e) => { if (e.pointerType === 'touch') { const now = Date.now(); if (now - lastTap < 350) closeWhite(); lastTap = now; } });
    $('nsWhiteClose').addEventListener('click', (e) => { e.stopPropagation(); closeWhite(); });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { closeWhite(); if (S.cur && S.cur.mode !== 'look') setEditMode('look'); }
        if (S.view === 'live' && (e.key === ' ' || e.key === 'Enter') && e.target === document.body) { e.preventDefault(); capture(); }
    });
    document.addEventListener('fullscreenchange', () => { if (!document.fullscreenElement && !$('nsWhitePanel').hidden) closeWhite(); });
    const det = $('nsLightHelp');
    det.addEventListener('toggle', () => { setPref('help', det.open ? 1 : 0); if (det.open) drawQr(); });
    window.addEventListener('resize', () => { if (S.view === 'edit') drawEditOverlay(); });

    // files
    const up = $('nsUpload');
    const handler = (files) => takeFiles(files);
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(up, (f, d) => handler((d && d.files) || (f ? [f] : [])));
    else up.addEventListener('tool-uploader:file', (e) => handler((e.detail && (e.detail.files || (e.detail.file && [e.detail.file]))) || []));

    document.addEventListener('visibilitychange', () => {
        if (document.hidden && S.view === 'live') stopCamera();
        else if (!document.hidden && S.view === 'live' && S.live && !S.live.stream && !S.live.opening && $('nsCamFail').hidden) startCamera();
    });
    window.addEventListener('pagehide', () => stopCamera());
}

function pickFiles() {
    const inp = document.querySelector('[data-uploader-for="nsUpload"]') || document.querySelector('#nsUpload input[type=file]');
    if (inp) inp.click();
}

function init() {
    S.film = pref('film', 'color');
    if (!C.MODES.includes(S.film)) S.film = 'color';
    S.fmt = pref('fmt', 'jpg') === 'png' ? 'png' : 'jpg';
    S.keepShooting = !!pref('keep', false);
    S.liveAdj = Object.assign(C.defaultAdjust(), (() => { const a = pref('adj', null); return a && typeof a === 'object' ? a : {}; })());
    S.liveAdj.neutral = null;
    bind();
    setFilm(S.film);
    goView('start');
    const det = $('nsLightHelp');
    if (+pref('help', 1) === 0) det.open = false; else drawQr();
    restore();
    DB.available().then((ok) => { if (!ok) funnel('no_idb', {}); });
    stage('ready');
    window.__negativeScanner = { S, C, FR, capture, saveCurrent, saveAll, openFrame, takeFiles, setFilm };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
